import { memo, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import ClipShareDownloadTab from './ClipShareDownloadTab';
import ClipShareLinkTab from './ClipShareLinkTab';
import useClipShareHook from './useClipShareHook';
import SharePopup, { SharePopupTab } from '../SharePopup';
import { useAppContext } from '@/context/AppContext/AppContext';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { ButtonVariation } from '@/components/atoms/Button/ButtonTypes';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

const allTabs: SharePopupTab[] = [
  { key: 'clip-share-link-tab', title: 'Link', tabComponent: <ClipShareLinkTab /> },
  { key: 'clip-share-download-tab', title: 'Download', tabComponent: <ClipShareDownloadTab /> }
];

const ClipSharePopup = ({
  activeClipId,
  downloadIntent,
  iconButtonVariation
}: {
  activeClipId?: string;
  downloadIntent?: boolean;
  iconButtonVariation?: ButtonVariation;
}) => {
  const sharePopupRef = useRef(null) as React.MutableRefObject<{ openSharePopup: () => void } | null>;

  const { adminAppStore } = useAppContext();
  const { setCurrentClipAsShared } = useClipShareHook();
  const { clipData } = useClipsContext();
  const hasDownloadParam = useRef(new URLSearchParams(globalThis.location.search).get('download') === 'true');

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isDownloadRevampEnabled = featureFlags[FeatureFlagKeys.Use_CL_Download_Revamp];

  const tabs = useMemo(() => {
    if (isDownloadRevampEnabled) {
      return [allTabs[0]];
    }
    return allTabs;
  }, [isDownloadRevampEnabled]);

  const [currentTab, setCurrentTab] = useState(tabs[0]);

  const beforeOpen = () => {
    adminAppStore.dispatch?.('user/showForceChangePasswordDialogIfRequired');
    if (!isDownloadRevampEnabled && (downloadIntent || hasDownloadParam.current)) {
      const downloadTab = tabs.find(tab => tab.key === 'clip-share-download-tab');
      setCurrentTab(downloadTab || tabs[0]);
    } else {
      setCurrentTab(tabs[0]);
    }

    setCurrentClipAsShared();
  };

  useEffect(() => {
    if (!isDownloadRevampEnabled && activeClipId === clipData.id && (downloadIntent || hasDownloadParam.current)) {
      sharePopupRef.current?.openSharePopup();
    }
  }, [downloadIntent, activeClipId, clipData.id, isDownloadRevampEnabled]);

  return (
    <SharePopup
      ref={sharePopupRef}
      currentTab={currentTab}
      tabs={tabs}
      iconButtonVariation={iconButtonVariation}
      beforeOpen={beforeOpen}
      setCurrentTab={setCurrentTab}
      content={isDownloadRevampEnabled ? '' : 'Share'}
      icon={isDownloadRevampEnabled ? 'IconShare3' : 'IconShare2'}
      showTooltip={isDownloadRevampEnabled}
    />
  );
};

export default memo(ClipSharePopup);
